import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/ui/form'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import styles from './EditPipelineModal.module.scss'
import { Button } from '@/ui/button'
import { useUpdateDataFlowMutation } from '@/features/adp/hooks/useUpdateDataFlowMutation'
import { setPipelineDetails } from '@/features/adp/state/pipelineSlice'

const editPipelineSchema = z.object({
  name: z.string().min(1, 'Pipeline/Job Name is required'),
  description: z.string().optional(),
})

type EditPipelineFormValues = z.infer<typeof editPipelineSchema>;

export type EditPipelineModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditPipelineModal({
  open,
  onOpenChange,
}: Readonly<EditPipelineModalProps>) {
  const dispatch = useAppDispatch()
  const { dataFlowId, pipelineName, pipelineDescription } = useAppSelector(state => state.pipeline)
  const updateDataFlowMutation = useUpdateDataFlowMutation()

  const form = useForm<EditPipelineFormValues>({
    resolver: zodResolver(editPipelineSchema),
    defaultValues: {
      name: pipelineName,
      description: pipelineDescription,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: pipelineName,
        description: pipelineDescription,
      })
    }
  }, [open, pipelineName, pipelineDescription, form])

  const onSubmit = (values: EditPipelineFormValues) => {
    const nextName = values.name.trim() || pipelineName
    const nextDescription = values.description?.trim() ?? ''

    updateDataFlowMutation.mutate({
      id: String(dataFlowId ?? ''),
      payload: {
        data_flow_name: nextName,
        description: nextDescription,
      },
    }, {
      onSuccess: () => {
        dispatch(
          setPipelineDetails({
            pipelineName: nextName,
            pipelineDescription: nextDescription,
          })
        )
        onOpenChange(false)
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(event) => {
                    event.preventDefault()
                }}
 >
        <DialogDescription className={styles.visuallyHidden}>
          Edit pipeline name and description.
        </DialogDescription>

        <div className={styles.header}>
          <DialogTitle className={styles.title}>Edit Title</DialogTitle>
        </div>

        <div className={styles.rule} aria-hidden />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.upperSection}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor='edit-pipeline-name'>Pipeline/Job Name</FormLabel>
                    <FormControl>
                      <Input
                        id="edit-pipeline-name"
                        placeholder="Enter your pipeline/job name"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        id="edit-pipeline-description"
                        placeholder="Enter your pipeline/job description"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className={styles.rule} aria-hidden />

            <div className={styles.footer}>
              <Button
                onClick={() => onOpenChange(false)}
                variant='outline'
                disabled={updateDataFlowMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateDataFlowMutation.isPending}>
                {updateDataFlowMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
